import { config } from '@static/js/env.config';
import { getNodeEnv } from '@shared/utils/getEnvironmentVariables';
import type { ThemeOutput } from './themeOutput.types';

/**
 * Fetches a theme's sprite JSON, deduping in-flight and settled requests per
 * `themeId|devicePixelRatio`.
 *
 * Replaces the legacy applicator's raw XHR (which re-downloaded the whole theme JSON on every
 * apply call — Brannan issues 2-3 per page) and its deprecated `document.domain === 'localhost'`
 * branch. URL contract is unchanged: same-origin `/assets/styles/<styleId>/style.json` — served
 * by the legacy app-server styleRoutes in prod, and by joy-web's own dev proxy
 * (src/server/server.ts `/assets/styles/:styleId/style.json`) in development.
 */

const themeStylesCache = new Map<string, Promise<ThemeOutput>>();

export const fetchThemeStyles = (styleId: string, resolution: number): Promise<ThemeOutput> => {
  const devicePixelDensity = Math.round(resolution || 1);
  const cacheKey = `${styleId}|${devicePixelDensity}`;

  const cached = themeStylesCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const { isDevelopment } = getNodeEnv();
  const search = new URLSearchParams({ pixel_ratio: String(devicePixelDensity) });
  const path = `/assets/styles/${encodeURIComponent(styleId)}/style.json?${search.toString()}`;
  // Mirrors URLS.styleApplicatorLib's env branch: absolute dev-server origin in development
  // (the page and the proxy can live on different origins in preview iframes), same-origin
  // relative in prod. The prod path stays relative on purpose — resolving it against an origin
  // would mean reading `window.location`, which this module must not do (SSR).
  const url = isDevelopment ? new URL(path, config.clientUri).toString() : path;

  const request = (async (): Promise<ThemeOutput> => {
    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) {
        throw new Error(`Failed to fetch theme styles for "${styleId}" (${response.status})`);
      }
      return (await response.json()) as ThemeOutput;
    } catch (error) {
      // Don't poison the cache with a transient failure — let a later apply retry.
      themeStylesCache.delete(cacheKey);
      throw error;
    }
  })();

  themeStylesCache.set(cacheKey, request);
  return request;
};

/** Test-only: reset the module-level cache between specs. */
export const clearThemeStylesCacheForTesting = () => {
  themeStylesCache.clear();
};
